import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { Bars3Icon } from "@heroicons/react/24/outline";
import PopupAlert from "../ui/PopupAlert";
import { useAuth } from "../../contexts/AuthContext";

const getInitials = (name = "") => {
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return "?";
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
};

const Navbar = ({ onOpenMobileNav, mobileNavOpen = false }) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const { user, handleLogout } = useAuth();
  const navigate = useNavigate();
  const profileRef = useRef(null);
  const initials = useMemo(() => getInitials(user?.name), [user?.name]);

  const handleClick = () => {
    handleLogout();
    navigate("/login");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <nav className="relative z-50 border-b border-slate-200 bg-slate-100 shadow-sm">
      <div className="w-full px-3 sm:px-6 lg:px-10">
        <div className="flex h-[60px] items-center gap-2">
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {typeof onOpenMobileNav === "function" && (
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg p-2 text-slate-700 hover:bg-slate-200 md:hidden"
                onClick={onOpenMobileNav}
                aria-label="Open navigation menu"
                aria-expanded={mobileNavOpen}
              >
                <Bars3Icon className="size-6" />
              </button>
            )}
            <Link to="/" className="min-w-0 shrink">
              <img
                src="/img/Logo_2.png"
                alt="USAX"
                className="h-7 w-auto sm:h-8"
              />
            </Link>
          </div>

          <div className="flex shrink-0 items-center justify-end gap-1.5 sm:gap-3">
            <PopupAlert />
            <div className="relative" ref={profileRef}>
              <button
                type="button"
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="group flex items-center space-x-2 rounded-lg px-1 py-1 focus:outline-none focus:ring-2 focus:ring-primary-200 sm:space-x-3 sm:px-1.5"
              >
                <div className="hidden flex-col items-end sm:flex">
                  <span className="max-w-[10rem] truncate text-sm font-medium text-slate-800">
                    {user.name}
                  </span>
                  <span className="text-xs text-primary">{user.user_type}</span>
                </div>
                <span
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-primary-200 bg-primary-100 text-sm font-semibold text-primary-800 transition duration-150 ease-in-out group-hover:border-primary-300 sm:h-10 sm:w-10"
                  aria-hidden="true"
                >
                  {initials}
                </span>
              </button>

              {isProfileOpen && (
                <div className="absolute right-0 z-50 mt-2 w-56 rounded-lg border border-slate-100 bg-white py-2 shadow-xl sm:w-48">
                  <div className="border-b border-slate-100 px-4 py-2">
                    <p className="text-sm font-medium text-slate-900 sm:hidden">
                      {user.name}
                    </p>
                    <p className="text-sm font-medium text-slate-900 max-sm:mt-0.5 max-sm:text-xs max-sm:text-slate-500">
                      Signed in as
                    </p>
                    <p className="truncate text-sm text-slate-600">
                      {user.email}
                    </p>
                  </div>

                  <div className="mt-1">
                    <button
                      type="button"
                      onClick={handleClick}
                      className="block w-full px-4 py-2 text-left text-sm text-primary transition duration-150 ease-in-out hover:bg-primary-100"
                    >
                      Sign out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
